<template>
  <HoppSmartModal v-if="show" dialog :title="t('team.edit')" @close="hideModal">
    <template #body>
      <div class="flex flex-col">
        <HoppSmartInput
          v-model="editingName"
          placeholder=" "
          :label="t('action.label')"
          input-styles="floating-input"
          @submit="saveTeam"
        />
        <div class="flex flex-1 items-center justify-between pt-4">
          <label for="memberList" class="p-4">
            {{ t("team.members") }}
          </label>
          <div class="flex">
            <HoppButtonSecondary
              :icon="IconUserPlus"
              :label="t('team.invite')"
              filled
              @click="
                () => {
                  emit('invite-team')
                }
              "
            />
          </div>
        </div>
        <div v-if="teamDetails.loading" class="rounded border border-divider">
          <div class="flex items-center justify-center p-4">
            <HoppSmartSpinner />
          </div>
        </div>
        <div
          v-if="
            !teamDetails.loading &&
            E.isRight(teamDetails.data) &&
            teamDetails.data.right.team?.teamMembers
          "
          class="rounded border border-divider"
        >
          <HoppSmartPlaceholder
            v-if="teamDetails.data.right.team.teamMembers.length === 0"
            :src="`/images/states/${colorMode.value}/add_group.svg`"
            :alt="`${t('empty.members')}`"
            :text="t('empty.members')"
          >
            <template #body>
              <HoppButtonSecondary
                :icon="IconUserPlus"
                :label="t('team.invite')"
                @click="
                  () => {
                    emit('invite-team')
                  }
                "
              />
            </template>
          </HoppSmartPlaceholder>
          <div v-else class="divide-y divide-dividerLight">
            <div
              v-for="(member, index) in membersList"
              :key="`member-${index}`"
              class="flex divide-x divide-dividerLight"
            >
              <input
                class="flex flex-1 bg-transparent px-4 py-2"
                :placeholder="`${t('team.email')}`"
                :name="'param' + index"
                :value="member.email"
                readonly
              />
              <span>
                <tippy
                  interactive
                  trigger="click"
                  theme="popover"
                  :on-shown="() => tippyActions![index].focus()"
                >
                  <HoppSmartSelectWrapper>
                    <input
                      class="flex flex-1 cursor-pointer bg-transparent px-4 py-2"
                      :placeholder="`${t('team.permissions')}`"
                      :name="'value' + index"
                      :value="member.role"
                      readonly
                    />
                  </HoppSmartSelectWrapper>
                  <template #content="{ hide }">
                    <div
                      ref="tippyActions"
                      class="flex flex-col focus:outline-none"
                      tabindex="0"
                      @keyup.escape="hide()"
                    >
                      <HoppSmartItem
                        label="OWNER"
                        :icon="
                          member.role === 'OWNER' ? IconCircleDot : IconCircle
                        "
                        :active="member.role === 'OWNER'"
                        @click="
                          () => {
                            updateAccessRole(
                              member.userID,
                              TeamAccessRole.Owner
                            )
                            hide()
                          }
                        "
                      />
                      <HoppSmartItem
                        label="EDITOR"
                        :icon="
                          member.role === 'EDITOR' ? IconCircleDot : IconCircle
                        "
                        :active="member.role === 'EDITOR'"
                        @click="
                          () => {
                            updateAccessRole(
                              member.userID,
                              TeamAccessRole.Editor
                            )
                            hide()
                          }
                        "
                      />
                      <HoppSmartItem
                        label="VIEWER"
                        :icon="
                          member.role === 'VIEWER' ? IconCircleDot : IconCircle
                        "
                        :active="member.role === 'VIEWER'"
                        @click="
                          () => {
                            updateAccessRole(
                              member.userID,
                              TeamAccessRole.Viewer
                            )
                            hide()
                          }
                        "
                      />
                    </div>
                  </template>
                </tippy>
              </span>
              <div>
                <HoppButtonSecondary
                  id="member"
                  v-tippy="{ theme: 'tooltip' }"
                  :title="t('action.remove')"
                  :icon="IconUserMinus"
                  color="red"
                  :loading="isLoadingIndex === index"
                  @click="removeExistingTeamMember(member.userID, index)"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="
            !teamDetails.loading &&
            E.isRight(teamDetails.data) &&
            teamDetails.data.right.team?.teamGroups.length
          "
          class="mt-6"
        >
          <label for="memberList" class="p-4">
            {{ t("additional.groups") }}
          </label>

          <div class="rounded border border-divider mt-4">
            <div class="divide-y divide-dividerLight">
              <div
                v-for="(group, index) in teamGroupsList"
                :key="`group-${index}`"
                class="flex divide-x divide-dividerLight"
              >
                <details class="flex-1 px-4 py-3 bg-transparent">
                  <summary class="flex">
                    <span
                      class="chevron-icon flex items-center mr-2 text-secondaryLight"
                    >
                      <icon-lucide-chevron-down class="details-chevron-down" />
                      <icon-lucide-chevron-right
                        class="details-chevron-right"
                      />
                    </span>
                    <div>
                      <p class="truncate">
                        {{ group.name }}
                      </p>
                      <p class="text-secondaryLight">
                        {{ group.description }}
                      </p>
                    </div>
                  </summary>
                  <div class="pl-4 ml-2 border-l border-secondaryLight">
                    <div v-if="group.users" class="overflow-y-auto mb-4">
                      <div
                        v-for="user in group.users"
                        :key="user.uid"
                        class="py-2 px-2 rounded items-center mb-3"
                      >
                        <p>{{ user.displayName || "Unnamed User" }}</p>
                        <span>{{ user.email }}</span>
                      </div>
                    </div>
                  </div>
                </details>

                <span>
                  <tippy
                    interactive
                    trigger="click"
                    theme="popover"
                    :on-shown="() => tippyActions![index].focus()"
                  >
                    <HoppSmartSelectWrapper>
                      <input
                        class="flex flex-1 cursor-pointer bg-transparent px-4 py-2"
                        :placeholder="`${t('team.permissions')}`"
                        :name="'value' + index"
                        :value="group.role"
                        readonly
                      />
                    </HoppSmartSelectWrapper>
                    <template #content="{ hide }">
                      <div
                        ref="tippyActions"
                        class="flex flex-col focus:outline-none"
                        tabindex="0"
                        @keyup.escape="hide()"
                      >
                        <HoppSmartItem
                          label="OWNER"
                          :icon="
                            group.role === 'OWNER' ? IconCircleDot : IconCircle
                          "
                          :active="group.role === 'OWNER'"
                          @click="
                            () => {
                              updateAccessRole(
                                group.id,
                                TeamAccessRole.Owner,
                                true
                              )
                              hide()
                            }
                          "
                        />
                        <HoppSmartItem
                          label="EDITOR"
                          :icon="
                            group.role === 'EDITOR' ? IconCircleDot : IconCircle
                          "
                          :active="group.role === 'EDITOR'"
                          @click="
                            () => {
                              updateAccessRole(
                                group.id,
                                TeamAccessRole.Editor,
                                true
                              )
                              hide()
                            }
                          "
                        />
                        <HoppSmartItem
                          label="VIEWER"
                          :icon="
                            group.role === 'VIEWER' ? IconCircleDot : IconCircle
                          "
                          :active="group.role === 'VIEWER'"
                          @click="
                            () => {
                              updateAccessRole(
                                group.id,
                                TeamAccessRole.Viewer,
                                true
                              )
                              hide()
                            }
                          "
                        />
                      </div>
                    </template>
                  </tippy>
                </span>
                <div>
                  <HoppButtonSecondary
                    id="group"
                    v-tippy="{ theme: 'tooltip' }"
                    :title="t('action.remove')"
                    :icon="IconUserMinus"
                    color="red"
                    :loading="isLoadingIndex === index"
                    @click="removeExistingTeamMember(group.id, index, true)"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="!teamDetails.loading && E.isLeft(teamDetails.data)"
            class="flex flex-col items-center"
          >
            <icon-lucide-help-circle class="svg-icons mb-4" />
            {{ t("error.something_went_wrong") }}
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <span class="flex space-x-2">
        <HoppButtonPrimary
          :label="t('action.save')"
          :loading="isLoading"
          outline
          @click="saveTeam"
        />
        <HoppButtonSecondary
          :label="t('action.cancel')"
          outline
          filled
          @click="hideModal"
        />
      </span>
    </template>
  </HoppSmartModal>
</template>

<script setup lang="ts">
import * as E from "fp-ts/Either"
import { computed, ref, watch } from "vue"
import {
  TeamMemberAddedDocument,
  TeamMemberRemovedDocument,
  TeamAccessRole,
  TeamMemberUpdatedDocument,
} from "@hoppscotch/common/helpers/backend/graphql"
import {
  GetTeamDocument,
  GetTeamQuery,
  GetTeamQueryVariables,
} from "@app/api/generated/graphql"
import {
  removeTeamMember,
  renameTeam,
  updateTeamAccessRole,
} from "@hoppscotch/common/helpers/backend/mutations/Team"
import {
  removeTeamUserGroups,
  updateTeamUserGroupsRole,
} from "@app/platform/teams/Team"
import { TeamNameCodec } from "@hoppscotch/common/helpers/backend/types/TeamName"

import { useI18n } from "@hoppscotch/common/composables/i18n"
import { useColorMode } from "@hoppscotch/common/composables/theming"
import { useToast } from "@hoppscotch/common/composables/toast"
import { useGQLQuery } from "@hoppscotch/common/composables/graphql"

import { TippyComponent } from "vue-tippy"
import IconCircle from "~icons/lucide/circle"
import IconCircleDot from "~icons/lucide/circle-dot"
import IconUserMinus from "~icons/lucide/user-minus"
import IconUserPlus from "~icons/lucide/user-plus"

const t = useI18n()
const colorMode = useColorMode()

const emit = defineEmits<{
  (e: "hide-modal"): void
  (e: "refetch-teams"): void
  (e: "invite-team"): void
}>()

// Template refs
const tippyActions = ref<TippyComponent[] | null>(null)

const props = defineProps<{
  show: boolean
  editingTeam: {
    name: string
  }
  editingTeamID: string
}>()

const toast = useToast()

const editingName = ref(props.editingTeam.name)

watch(
  () => props.editingTeam.name,
  (newName: string) => {
    editingName.value = newName
  }
)

watch(
  () => props.editingTeamID,
  (teamID: string) => {
    teamDetails.execute({ teamID })
  }
)

const teamDetails = useGQLQuery<GetTeamQuery, GetTeamQueryVariables, "">({
  query: GetTeamDocument,
  variables: {
    teamID: props.editingTeamID,
  },
  pollDuration: 10000,
  pollLoadingEnabled: false, // when polling loading state won't be updated
  defer: true,
  updateSubs: computed(() => {
    if (props.editingTeamID) {
      return [
        {
          key: 1,
          query: TeamMemberAddedDocument,
          variables: {
            teamID: props.editingTeamID,
          },
        },
        {
          key: 2,
          query: TeamMemberUpdatedDocument,
          variables: {
            teamID: props.editingTeamID,
          },
        },
        {
          key: 3,
          query: TeamMemberRemovedDocument,
          variables: {
            teamID: props.editingTeamID,
          },
        },
      ]
    }
    return []
  }),
})

watch(
  () => props.show,
  (show) => {
    if (!show) {
      teamDetails.pause()
    } else {
      teamDetails.unpause()
    }
  }
)

const roleUpdates = ref<
  {
    id: string
    role: TeamAccessRole
    isGroup?: boolean
  }[]
>([])

watch(
  () => teamDetails,
  () => {
    if (teamDetails.loading) return

    const data = teamDetails.data

    if (E.isRight(data)) {
      const members = data.right.team?.teamMembers ?? []
      const groups = data.right.team?.teamGroups ?? []

      // Remove deleted members
      roleUpdates.value = roleUpdates.value.filter((update) =>
        update.isGroup
          ? groups.findIndex((y) => y.id === update.id) !== -1
          : members.findIndex((y) => y.user.uid === update.id) !== -1
      )
    }
  }
)

const updateAccessRole = (
  id: string,
  role: TeamAccessRole,
  isGroup = false
) => {
  const updateIndex = roleUpdates.value.findIndex(
    (item) => item.id === id && item.isGroup === isGroup
  )
  if (updateIndex !== -1) {
    // Role Update exists
    roleUpdates.value[updateIndex].role = role
  } else {
    // Role Update does not exist
    roleUpdates.value.push({
      id,
      role,
      isGroup,
    })
  }
}

const membersList = computed(() => {
  if (teamDetails.loading) return []

  const data = teamDetails.data

  if (E.isLeft(data)) return []

  if (E.isRight(data)) {
    const members = (data.right.team?.teamMembers ?? []).map((member) => {
      const updatedRole = roleUpdates.value.find(
        (update) => update.id === member.user.uid
      )

      return {
        userID: member.user.uid,
        email: member.user.email!,
        role: updatedRole?.role ?? member.role,
      }
    })

    return members
  }

  return []
})

const teamGroupsList = computed(() => {
  if (teamDetails.loading) return []

  const data = teamDetails.data

  if (E.isLeft(data)) return []

  if (E.isRight(data)) {
    const groups = (data.right.team?.teamGroups ?? []).map((group) => {
      const updatedRole = roleUpdates.value.find(
        (update) => update.id === group.id && update.isGroup === true
      )

      return {
        id: group.id,
        role: updatedRole?.role ?? group.role,
        name: group.userGroup.name,
        description: group.userGroup.description,
        users: group.userGroup.users,
        usersCount: group.userGroup.userCount,
      }
    })

    return groups
  }

  return []
})

const isLoadingIndex = ref<null | number>(null)

const removeExistingTeamMember = async (
  id: string,
  index: number,
  isGroup = false
) => {
  isLoadingIndex.value = index

  let result
  if (isGroup) {
    console.log("Removing group", id)
    const removeTeamGroupResult = await removeTeamUserGroups(
      id,
      props.editingTeamID
    )()
    result = removeTeamGroupResult
  } else {
    const removeTeamMemberResult = await removeTeamMember(
      id,
      props.editingTeamID
    )()
    result = removeTeamMemberResult
  }

  if (E.isLeft(result)) {
    toast.error(`${t("error.something_went_wrong")}`)
  } else {
    toast.success(
      isGroup ? `${t("team.group_removed")}` : `${t("team.member_removed")}`
    )
    emit("refetch-teams")
    teamDetails.execute({ teamID: props.editingTeamID })
  }
  isLoadingIndex.value = null
}

const isLoading = ref(false)

const saveTeam = async () => {
  isLoading.value = true
  if (editingName.value !== "") {
    if (TeamNameCodec.is(editingName.value)) {
      const updateTeamNameResult = await renameTeam(
        props.editingTeamID,
        editingName.value
      )()
      if (E.isLeft(updateTeamNameResult)) {
        toast.error(`${t("error.something_went_wrong")}`)
      } else {
        // Process member role updates
        for (const update of roleUpdates.value) {
          if (update.isGroup) {
            console.log("Updating group role", update)

            // Update group role
            const updateGroupRoleResult = await updateTeamUserGroupsRole(
              update.id,
              props.editingTeamID,
              update.role
            )()
            if (E.isLeft(updateGroupRoleResult)) {
              toast.error(`${t("error.something_went_wrong")}`)
              console.error(updateGroupRoleResult.left.error)
            }
          } else {
            // Update user role
            const updateAccessRoleResult = await updateTeamAccessRole(
              update.id,
              props.editingTeamID,
              update.role
            )()
            if (E.isLeft(updateAccessRoleResult)) {
              toast.error(`${t("error.something_went_wrong")}`)
              console.error(updateAccessRoleResult.left.error)
            }
          }
        }
      }
      hideModal()
      toast.success(`${t("team.saved")}`)
    } else {
      toast.error(`${t("team.name_length_insufficient")}`)
    }
  } else {
    toast.error(`${t("empty.team_name")}`)
  }
  isLoading.value = false
}

const hideModal = () => {
  emit("hide-modal")
}
</script>

<style scoped lang="scss">
details > summary {
  list-style: none;
}
details > summary::-webkit-details-marker {
  display: none;
}

.details-chevron-down {
  display: none;
}

.details-chevron-right {
  display: block;
}

details[open] .details-chevron-down {
  display: block;
}

details[open] .details-chevron-right {
  display: none;
}
</style>
